import { ActionMenu, Box } from '@primer/react';
import { DotFillIcon } from '@primer/octicons-react';
import { CurrencyDollarSimple, Spinner } from 'phosphor-react';

type DropdownMorphedProps = {
  children: React.ReactNode;
  status: string;
  creditsLoading: boolean;
  credits: any;
};

export const DropdownMorphed: React.FC<DropdownMorphedProps> = ({
  children,
  status,
  creditsLoading,
  credits,
}) => {
  // Dynamic color based on status
  const statusColor =
    status === 'running' || status === 'cold-starting' || status === 'initializing'
      ? '#14C078'
      : status === 'idle'
      ? '#ffc107'
      : status === 'initializing' || status === 'deploying'
      ? '#bdbdbd'
      : 'grey'; // Stopped or Error state

  // Function to extract and format credits value
  const getCreditsDisplay = () => {
    if (creditsLoading) return null;

    // Handle different possible credit data structures
    if (credits === null || credits === undefined) {
      return '0';
    }

    // If credits is a number
    if (typeof credits === 'number') {
      return credits.toFixed(2);
    }

    // If credits is a string
    if (typeof credits === 'string') {
      return credits;
    }

    // If credits is an object, try common property names
    if (typeof credits === 'object') {
      // Try common credit property names
      const possibleKeys = ['credits', 'balance', 'amount', 'value', 'remaining', 'available'];

      for (const key of possibleKeys) {
        if (credits[key] !== undefined && credits[key] !== null) {
          const value = credits[key];
          return typeof value === 'number' ? value.toFixed(2) : String(value);
        }
      }

      // If it's an object but no recognized keys, stringify it for debugging
      return JSON.stringify(credits);
    }

    return String(credits);
  };

  const creditsDisplay = getCreditsDisplay();

  return (
    <ActionMenu>
      <ActionMenu.Button
        disabled={creditsLoading}
        alignContent="center"
        sx={{
          border: `1px solid #464b51`,
          borderRadius: '6px',
          fontSize: '12px',
          backgroundColor:
            'var(--bgColor-neutral-muted, var(--color-neutral-subtle, rgba(99,110,123,0.1)))',
        }}
      >
        {creditsLoading ? (
          <Spinner size={18} className="animate-spin" />
        ) : (
          <Box className="flex items-center gap-1">
            <DotFillIcon fill={statusColor} />
            <span className="flex items-center gap-1">
              Credits:
              <p className="flex items-center">
                <CurrencyDollarSimple /> {creditsDisplay}
              </p>
            </span>
          </Box>
        )}
      </ActionMenu.Button>
      <ActionMenu.Overlay
        height="fit-content"
        align="end"
        sx={{
          borderRadius: '0.25rem',
          backgroundColor: '#16191D',
          width: '300px',
        }}
      >
        <Box className="flex flex-col h-full overflow-y-auto">
          <p className="text-sm border-b border-[#636C76] px-5 py-2">Active Resources</p>
          <Box className="flex flex-col gap-3.5 overflow-y-auto" sx={{ maxHeight: '330px' }}>
            {children}
          </Box>
        </Box>
      </ActionMenu.Overlay>
    </ActionMenu>
  );
};
